<template>
    <form class="centered-message-container" @submit.prevent @mousedown="onClickOutside" @touchdown="onClickOutside">
        <div class="centered-message" data-testid="centered-message" :class="{'single-button': centeredMessage.buttons.length === 1, 'few-buttons': centeredMessage.buttons.length <= 2, 'multiple-buttons': centeredMessage.buttons.length > 2}" @mousedown.stop="" @touchdown.stop="">
            <div class="header">
                <Spinner v-if="centeredMessage.type === 'loading'" class="" />
                <img v-else-if="centeredMessage.type === 'clock'" class="center" src="@stamhoofd/assets/images/illustrations/clock.svg">
                <img v-else-if="centeredMessage.type === 'health'" class="center" src="@stamhoofd/assets/images/illustrations/health-data.svg">
                <img v-else-if="centeredMessage.type === 'sync'" class="center" src="@stamhoofd/assets/images/illustrations/sync.svg">
                <span v-else-if="centeredMessage.type !== 'none'" :class="'center icon '+centeredMessage.type" />
            </div>

            <h1>
                {{ centeredMessage.title }}
            </h1>
            <p>{{ centeredMessage.description }}</p>

            <STErrorsDefault :error-box="errors.errorBox" />

            <div v-if="centeredMessage.checkbox" class="checkbox-container">
                <Checkbox v-model="isChecked" error-fields="isChecked">
                    {{ centeredMessage.checkbox.text }}
                </Checkbox>
            </div>

            <div class="buttons" :class="{'single-button': centeredMessage.buttons.length === 1, 'multiple-buttons': centeredMessage.buttons.length > 2}">
                <LoadingButton v-for="(button, index) in centeredMessage.buttons" :key="index" :loading="button.loading">
                    <ProgressOutline :progress="getProgress(button)" :radius="13" class="button-progress-outline" :class="button.type">
                        <a v-if="button.href" ref="buttons" :href="button.href" class="button full" :class="button.type" data-testid="centered-message-button" :disabled="button.disabled || (button.requireAcceptCheckbox && !isChecked)" @click="onClickButton(button)">
                            <span v-if="button.icon" class="icon" :class="button.icon" />
                            <span>{{ button.text }}</span>
                        </a>
                        <button v-else ref="buttons" class="button full wrap" :class="button.type" type="button" :tabindex="0" data-testid="centered-message-button" :disabled="button.disabled || (button.requireAcceptCheckbox && !isChecked)" @click="onClickButton(button)">
                            <span v-if="button.icon" class="icon" :class="button.icon" />
                            <span>{{ button.text }}</span>
                        </button>
                    </ProgressOutline>
                </LoadingButton>
            </div>
        </div>
    </form>
</template>

<script lang="ts" setup>
import { usePop } from '@browser/navigation';

import { ErrorBox } from '../errors/ErrorBox';
import STErrorsDefault from '../errors/STErrorsDefault.vue';
import LoadingButton from '../navigation/LoadingButton.vue';
import ProgressOutline from '../ProgressOutline.vue';
import Spinner from '../Spinner.vue';
import type { CenteredMessage, CenteredMessageButton } from './CenteredMessage';
import { onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useErrors } from '../errors/useErrors';

const props = defineProps<{
    centeredMessage: CenteredMessage;
}>();
const isClosing = ref(false);
const errors = useErrors();
const pop = usePop();
const buttonsRef = useTemplateRef<HTMLButtonElement[] | HTMLButtonElement | null>('buttons');
const isChecked = ref(false);
const now = ref(Date.now());
let animationFrame: number | null = null;

function tick() {
    now.value = Date.now();
    if (props.centeredMessage.buttons.some(b => b.availableAt && b.availableAt > now.value)) {
        animationFrame = requestAnimationFrame(tick);
    } else {
        animationFrame = null;
    }
}

const outlineShownSince = new Map<CenteredMessageButton, number>();
const catchUpDuration = 400;

function easeOut(t: number) {
    return 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
}

function getProgress(button: CenteredMessageButton) {
    if (!button.availableAt || !button.availabilityDelay || (button.requireAcceptCheckbox && !isChecked.value)) {
        outlineShownSince.delete(button);
        return 100;
    }
    let shownSince = outlineShownSince.get(button);
    if (shownSince === undefined) {
        shownSince = now.value;
        outlineShownSince.set(button, shownSince);
    }
    const progress = easeOut(1 - (button.availableAt - now.value) / button.availabilityDelay);

    // When the outline appears mid-delay, ramp up from 0 to the real progress instead of jumping
    const catchUp = easeOut((now.value - shownSince) / Math.min(catchUpDuration, button.availableAt - shownSince));
    return progress * catchUp * 100;
}

watch(() => props.centeredMessage.buttons.map(b => b.availableAt), () => {
    if (animationFrame === null) {
        tick();
    }
}, { immediate: true });

onBeforeUnmount(() => {
    if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
    }
});

onMounted(() => {
    props.centeredMessage.doHide = () => {
        close().catch(console.error);
    };

    if (document.activeElement && (document.activeElement as any).blur) {
        (document.activeElement as any).blur();
    }
    setTimeout(() => {
        focusNextButton();
    }, 200);
});

function onClickOutside() {
    // If this is a touch device, do nothing
    if (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || ((navigator as any).msMaxTouchPoints > 0)) {
        return;
    }
    dismiss();
}

async function onClickButton(button: CenteredMessageButton) {
    if (isClosing.value) {
        return;
    }
    if (button.disabled) {
        return;
    }
    if (button.loading) {
        return;
    }
    if (button.action) {
        button.loading = true;
        try {
            await button.action();
        } catch (e) {
            errors.errorBox = new ErrorBox(e);
            button.loading = false;
            return;
        }
        errors.errorBox = null;
        button.loading = false;
    }
    await close();
}

function dismiss() {
    const closeButton = props.centeredMessage.buttons.find(b => b.type === 'secundary');
    if (!closeButton) {
        return;
    }

    onClickButton(closeButton).catch(console.error);
}

async function close() {
    if (isClosing.value) {
        return;
    }
    isClosing.value = true;
    await pop({ force: true });
}

onActivated(() => {
    document.addEventListener('keydown', onKey);
});

onDeactivated(() => {
    document.removeEventListener('keydown', onKey);
});

function getButtons() {
    let buttons = buttonsRef.value;

    if (!buttons) {
        return [];
    }

    if (!Array.isArray(buttons)) {
        buttons = [buttons];
    }
    return buttons;
}

function focusNextButton() {
    const buttons = getButtons();
    if (buttons.length === 0) {
        return;
    }

    // Find first focused button and select the next one or first one if it is the last one
    let focusedButton = buttons.findIndex((b: any) => b === document.activeElement);

    if (focusedButton === -1) {
        focusedButton = buttons.findIndex((b: any) => b.classList.contains('focus-visible'));
    }

    let button = buttons[0];
    if (focusedButton !== -1) {
        if (focusedButton < buttons.length - 1) {
            button = buttons[focusedButton + 1];
        } else {
            button = buttons[0];
        }
    }

    // Fix unreliable focus visible
    if (focusedButton !== -1 && buttons[focusedButton]) {
        buttons[focusedButton].classList.remove('focus-visible');
    }
    button.classList.add('focus-visible');

    // And we'll remove it again on blur, once
    button.addEventListener('blur', function () {
        button.classList.remove('focus-visible');
    }, { once: true });

    button.focus();
}

function onKey(event: KeyboardEvent) {
    if (event.defaultPrevented || event.repeat) {
        return;
    }

    const key = event.key || event.keyCode;
    const closeButton = props.centeredMessage.buttons.find(b => b.type === 'secundary');

    if (key === 'Tab') {
        focusNextButton();
        event.preventDefault();
        return;
    }

    if (key === 'Escape' || key === 'Esc' || key === 27) {
        if (!closeButton) {
            return;
        }

        onClickButton(closeButton).catch(console.error);
        event.preventDefault();
        return;
    }

    if (key === 'Enter' || key === 13) {
        const focusedButton = getButtons().find((b: any) => b === document.activeElement);
        if (focusedButton) {
            // Browser default
            return;
        }

        const next = getButtons().find((b: HTMLElement) => b.classList.contains('focus-visible'));
        if (next) {
            next.click();
            event.preventDefault();
            return;
        }

        // Do we have a default action?
        const defaultButton = props.centeredMessage.buttons[0];
        if (defaultButton) {
            onClickButton(defaultButton).catch(console.error);
            event.preventDefault();
            return;
        }
    }
}
</script>

<style lang="scss">
@use "@stamhoofd/scss/base/variables.scss" as *;
@use '@stamhoofd/scss/base/text-styles.scss';

.centered-message {
    position: fixed;
    z-index: 10000;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    transform: translate(calc(-50% + (var(--st-safe-area-left, 0px) - var(--st-safe-area-right, 0px)) / 2), calc(-50% + (var(--st-safe-area-top, 0px) - var(--st-safe-area-bottom, 0px)) / 2));
    @extend %style-overlay-shadow;
    border-radius: $border-radius-modals;
    background: $color-background;
    max-width: calc(100vw - 30px);
    width: 350px;

    box-sizing: border-box;
    max-height: 100vh;
    overflow: auto;
    overflow-x: hidden;

    .checkbox-container {
        padding-top: 15px;
    }

    // Matches the colors of a disabled button
    .button-progress-outline {
        &.primary, &.secundary {
            color: $color-gray-2;
        }

        &.destructive {
            color: $color-error;

            .progress-outline-svg > rect.bar {
                opacity: 0.4;
            }
        }
    }

    &.few-buttons {

        @media (max-width: 551px) {
            padding: 20px;

            .buttons {
                display: flex;
                flex-direction: column;
                gap: 7px;
                padding-top: 20px;
            }
        }

        @media (min-width: 550px) {
            width: 550px;
            padding: 30px 40px;

            .buttons {
                border-top: $border-width-thin solid $color-border-shade;
                padding: 15px 40px 15px 40px;
                margin: 25px -40px -30px -40px;
                display: flex;
                flex-wrap: wrap;
                justify-content: flex-start;
                flex-direction: row-reverse;
                gap: 7px;
            }

        }
    }

    &.multiple-buttons {
        padding: 20px;

        .buttons {
            display: flex;
            flex-direction: column;
            gap: 7px;
            padding-top: 20px;

            > * {
                max-width: none;
            }
        }
    }

    .buttons.multiple-buttons {
        display: flex;
        flex-direction: column;
        justify-items: stretch;
        align-items: stretch;
        gap: 7px;
        padding-top: 20px;
    }

    > *:first-child {
        margin-top: 10px;
    }

    > img.center, > .icon.center {
        display: block;
        margin: 0 auto;
    }

    > .loading-button {
        display: block;
    }

    > h1 {
        padding-bottom: 10px;
        text-align: left;
        @extend %style-title-2;

        + p {
            @extend %style-description-small;
            white-space: pre-wrap;
        }
    }
}

.centered-message-container {
    position: fixed;
    z-index: 10000;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.3);
    opacity: 1;

    @media (prefers-color-scheme: dark) {
        background: rgba(8, 8, 8, 0.7);
    }

    &.show-enter-active {
        transition: opacity 0.30s;

        > .centered-message  {
            transition: transform 0.30s cubic-bezier(0.0, 0.0, 0.2, 1);
        }
    }

    &.show-leave-active {
        transition: opacity 0.25s;

        > .centered-message  {
            transition: transform 0.25s cubic-bezier(0.4, 0.0, 1, 1);
        }
    }

    &.show-enter-from, &.show-leave-to /* .fade-leave-active below version 2.1.8 */ {
        // Instant appearing context menu! (only leave animation)
        opacity: 0;

        > .centered-message  {
            @media (not (prefers-reduced-motion)) {
                transform: translate(-50%, 50vh);
            }

        }
    }
}

</style>
